import React, { useEffect } from 'react';
import { Form, Input, Button } from 'antd';
import { useTranslation } from 'react-i18next'; // 🟢 Import hook
import { useCity } from '../../store/cityContext';
import { City } from '../../types/city';
import isEqual from 'lodash/isEqual';
import showAlert from '../../common/Alert';
import { useAuth } from '../../store/AuthContext';

interface Props {
    selectedCity: City | null;
    setSelectedCity: (city: City | null) => void;
}

const CityForm: React.FC<Props> = ({ selectedCity, setSelectedCity }) => {
    const [form] = Form.useForm<City>();
    const { addCity, updateCity } = useCity();
    const { hasPermission } = useAuth();
    const { t } = useTranslation(); // 🟢 Access translation function

    useEffect(() => {
        if (selectedCity) {
            form.setFieldsValue(selectedCity);
        } else {
            form.resetFields();
        }
    }, [selectedCity, form]);

    const onFinish = async (values: City) => {
        try {
            if (selectedCity) {
                const merged = { ...selectedCity, ...values };
                const noChanges = isEqual(selectedCity, merged);

                if (noChanges) {
                    showAlert({
                        title: t('alerts.noChangesTitle') as string,
                        text: t('alerts.noChangesText') as string,
                        icon: 'warning'
                    });
                    return;
                }

                if (!selectedCity.id) {
                    showAlert({
                        title: t('alerts.missingIdTitle') as string,
                        text: t('alerts.missingIdText') as string,
                        icon: 'error'
                    });
                    return;
                }

                await updateCity({ ...values, id: selectedCity.id });
                showAlert({
                    title: t('alerts.updateSuccessTitle') as string,
                    text: t('alerts.updateSuccessText') as string,
                    icon: 'success'
                });
            } else {
                await addCity(values);
                showAlert({
                    title: t('alerts.addSuccessTitle') as string,
                    text: t('alerts.addSuccessText') as string,
                    icon: 'success'
                });
            }

            form.resetFields();
            setSelectedCity(null);
        } catch (error) {
            // Error handled by UI alert
            showAlert({
                title: t('alerts.operationFailedTitle') as string,
                text: t('alerts.operationFailedText') as string,
                icon: 'error'
            });
        }
    };
    const canSubmit = selectedCity
        ? hasPermission("city.update")
        : hasPermission("city.create");

    const submitLabel = selectedCity
        ? t('form.updateCity')
        : t('form.addCity');

    return (
        <Form<City> form={form} layout="vertical" onFinish={onFinish}>
            <Form.Item name="name_ku" label={t('form.cityNameKurdish')} rules={[{ required: true }]}>
                <Input />
            </Form.Item>
            <Form.Item name="name_eng" label={t('form.cityNameEnglish')} rules={[{ required: true }]}>
                <Input />
            </Form.Item>
            <Form.Item name="name_ar" label={t('form.cityNameArabic')} rules={[{ required: true }]}>
                <Input />
            </Form.Item>
            {canSubmit && (
                <Form.Item>
                    <Button type="primary" htmlType="submit" block>
                        {submitLabel}
                    </Button>
                </Form.Item>
            )}
        </Form>
    );
};

export default CityForm;
